import { config } from '@/config/config';
import axios from 'axios';

export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
});

// Helper to get token - can be set externally
let getTokenFn: (() => Promise<string | null>) | null = null;

export function setTokenGetter(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

// Add auth token to requests
apiClient.interceptors.request.use(
  async (config) => {
    try {
      let token: string | null = null;

      // Try using the token getter function first
      if (getTokenFn) {
        token = await getTokenFn();
      }

      // Fallback to window.Clerk
      if (!token && window.Clerk?.session) {
        token = await window.Clerk.session.getToken();
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be invalid, Clerk will handle re-authentication
      console.warn('Unauthorized request - token may be invalid');
    }
    return Promise.reject(error);
  }
);

const CREDENTIALS_ERROR_PHRASE =
  'Could not load credentials from any providers';
const CREDENTIALS_USER_MESSAGE =
  'Server is missing storage credentials. Please try again later or contact support.';

function sanitizeApiMessage(raw: string): string {
  if (raw.includes(CREDENTIALS_ERROR_PHRASE)) return CREDENTIALS_USER_MESSAGE;
  return raw;
}

/** Get a user-friendly message from an axios/API error. */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  if (!error || typeof error !== 'object') return fallback;
  const err = error as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    const msg = typeof d.message === 'string' ? d.message.trim() : '';
    if (msg) return sanitizeApiMessage(msg);
    if (typeof d.error === 'string' && d.error.trim())
      return sanitizeApiMessage(d.error.trim());
    if (Array.isArray(d.errors) && d.errors.length > 0) {
      const first = d.errors[0];
      return typeof first === 'string' ? sanitizeApiMessage(first) : fallback;
    }
  }
  const status = err.response?.status;
  if (status === 401) return 'Please sign in again and try creating your pet.';
  if (status === 403) return 'You don’t have permission to create a pet.';
  if (status === 404) return 'Service not found. Please try again later.';
  if (status && status >= 500)
    return 'Server error. Please try again in a moment.';
  if (err.message && typeof err.message === 'string') {
    if (err.message === 'Network Error')
      return 'Cannot reach the server. Check your connection or try again later.';
    return sanitizeApiMessage(err.message);
  }
  return fallback;
}
